// frontend/src/components/ConversationAnalytics.js
import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Chart from 'react-apexcharts';
import { Card, Col, Dropdown, Spinner, Alert, Button, Row } from 'react-bootstrap';
import { Facebook, Instagram, Phone, Settings, BarChart3, TrendingUp, Users } from 'lucide-react';
import CustomToggle from './dropdowns';

const ConversationAnalytics = ({ facebookUserCount, instagramUserCount, whatsappUserCount }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { analyticsData, loading, error } = useSelector((state) => state.chart);
  const { profile } = useSelector((state) => state.userservice);
  const [selectedPeriod, setSelectedPeriod] = useState('all');

  // Create deep cloned data to prevent mutations
  const safeAnalyticsData = useMemo(() => {
    if (!analyticsData) return null;
    return JSON.parse(JSON.stringify(analyticsData));
  }, [analyticsData]);

  // Pie chart options with updated colors
  const pieChartOptions = useMemo(
    () => ({
      labels: ['Facebook', 'Instagram', 'WhatsApp'],
      legend: { show: true, position: 'bottom', horizontalAlign: 'center' },
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: { width: 200 },
            legend: { show: false },
          },
        },
      ],
      colors: ['#3b5998', '#C13584', '#25D366'], // Facebook, Instagram, WhatsApp colors
    }),
    []
  );

  // Line chart options with updated theme color
  const lineChartOptions = useMemo(
    () => ({
      colors: ['#4091CC'],
      chart: {
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: false,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: false,
            reset: true,
          },
        },
        type: 'line',
        height: 350,
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800,
          animateGradually: {
            enabled: true,
            delay: 150,
          },
        },
      },
      stroke: {
        width: 3,
        curve: 'smooth',
      },
      grid: {
        show: true,
        strokeDashArray: 5,
        borderColor: '#e7e7e7',
      },
      markers: {
        size: 6,
        colors: '#FFFFFF',
        strokeColors: ['#4091CC'],
        strokeWidth: 2,
        strokeOpacity: 0.9,
        shape: 'circle',
        radius: 2,
        hover: { size: 8, sizeOffset: 3 },
      },
      xaxis: {
        type: 'category',
        categories: safeAnalyticsData?.lineChart?.labels || [],
        labels: {
          style: { colors: '#7E8B9A', fontSize: '12px' },
          rotate: -45,
          rotateAlways: false,
        },
        axisBorder: { show: true, color: '#e7e7e7' },
        axisTicks: { show: true, color: '#e7e7e7' },
      },
      yaxis: {
        labels: {
          style: { colors: '#7E8B9A', fontSize: '12px' },
          formatter: (value) => Math.floor(value).toLocaleString(),
        },
        title: {
          text: 'Number of Conversations',
          style: { color: '#7E8B9A', fontSize: '12px' },
        },
      },
      dataLabels: { enabled: false },
      tooltip: {
        enabled: true,
        shared: true,
        intersect: false,
        theme: 'light',
        style: { fontSize: '12px' },
        x: { format: 'dd MMM yyyy' },
        y: {
          formatter: (value) => `${value.toLocaleString()} conversations`,
        },
      },
      legend: {
        show: true,
        position: 'top',
        horizontalAlign: 'right',
        fontSize: '12px',
        labels: { colors: '#7E8B9A' },
      },
    }),
    [safeAnalyticsData]
  );

  // Chart series with proper immutability
  const pieChartSeries = useMemo(
    () => [facebookUserCount || 0, instagramUserCount || 0, whatsappUserCount || 0],
    [facebookUserCount, instagramUserCount, whatsappUserCount]
  );

  const lineChartSeries = useMemo(() => {
    if (!safeAnalyticsData?.lineChart?.series) {
      return [{ name: 'Total Conversations', data: [] }];
    }
    
    return safeAnalyticsData.lineChart.series.map(series => ({
      name: series.name,
      data: [...series.data],
    }));
  }, [safeAnalyticsData]);

  // Summary statistics
  const summaryStats = useMemo(() => {
    if (!safeAnalyticsData?.lineChart?.series?.[0]?.data) {
      return { total: 0, growth: 0, average: 0 };
    }
    const data = [...safeAnalyticsData.lineChart.series[0].data];
    const total = data[data.length - 1] || 0;
    const previous = data[data.length - 2] || 0;
    const growth = previous ? ((total - previous) / previous * 100) : 0;
    const average = data.length ? data.reduce((sum, val) => sum + val, 0) / data.length : 0;
    return { total, growth, average };
  }, [safeAnalyticsData]);

  // Period filter options
  const periodOptions = [
    { key: 'all', label: 'All Time' },
    { key: 'last30', label: 'Last 30 Days' },
    { key: 'last7', label: 'Last 7 Days' },
    { key: 'today', label: 'Today' },
  ];

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
  };

  // Handle settings navigation
  const handleSettingsClick = () => {
    navigate('/Settings');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="mb-8">
        {/* Platform Connections Section */}
        <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
              <Users size={24} className="text-[#4091CC]" />
              Platform Connections
            </h3>
            <button
              onClick={handleSettingsClick}
              className="flex items-center gap-2 px-4 py-2 text-[#4091CC] border border-[#4091CC] rounded-lg hover:bg-[#4091CC] hover:text-white transition-colors duration-200 font-medium"
            >
              <Settings size={16} />
              Settings
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Facebook */}
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl border border-blue-200">
              <div className="flex items-center">
                <div className="p-2 bg-blue-600 rounded-lg mr-4">
                  <Facebook size={20} className="text-white" />
                </div>
                <span className="font-semibold text-gray-800">Facebook</span>
              </div>
              <span className={`px-4 py-2 rounded-full text-sm font-medium ${
                profile?.Fblogin 
                  ? 'bg-green-100 text-green-800 border border-green-200' 
                  : 'bg-red-100 text-red-800 border border-red-200'
              }`}>
                {profile?.Fblogin ? 'Connected' : 'Disconnected'}
              </span>
            </div>

            {/* Instagram */}
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-pink-50 to-pink-100 rounded-xl border border-pink-200">
              <div className="flex items-center">
                <div className="p-2 bg-gradient-to-r from-pink-500 to-purple-600 rounded-lg mr-4">
                  <Instagram size={20} className="text-white" />
                </div>
                <span className="font-semibold text-gray-800">Instagram</span>
              </div>
              <span className={`px-4 py-2 rounded-full text-sm font-medium ${
                profile?.iglogin 
                  ? 'bg-green-100 text-green-800 border border-green-200' 
                  : 'bg-red-100 text-red-800 border border-red-200'
              }`}>
                {profile?.iglogin ? 'Connected' : 'Disconnected'}
              </span>
            </div>

            {/* WhatsApp */}
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-green-100 rounded-xl border border-green-200">
              <div className="flex items-center">
                <div className="p-2 bg-green-600 rounded-lg mr-4">
                  <Phone size={20} className="text-white" />
                </div>
                <span className="font-semibold text-gray-800">WhatsApp</span>
              </div>
              <span className={`px-4 py-2 rounded-full text-sm font-medium ${
                profile?.wpLogin 
                  ? 'bg-green-100 text-green-800 border border-green-200' 
                  : 'bg-red-100 text-red-800 border border-red-200'
              }`}>
                {profile?.wpLogin ? 'Connected' : 'Disconnected'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-6">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <TrendingUp size={20} className="text-red-600" />
              </div>
              <h4 className="text-lg font-semibold text-red-800">Error Loading Data</h4>
            </div>
            <p className="text-red-700 mb-4">{error}</p>
            <button
              onClick={() => navigate('/dashboards')}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200"
            >
              <TrendingUp size={16} />
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Line Chart */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <h5 className="text-xl font-semibold text-gray-800 mb-1">Conversation Trends</h5>
                  <p className="text-gray-600">Track conversation growth over time</p>
                </div>
                {safeAnalyticsData && (
                  <Dropdown>
                    <Dropdown.Toggle as={CustomToggle} id="period-dropdown" className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 text-gray-700 hover:bg-gray-100">
                      <i className="fas fa-calendar me-2"></i>
                      {periodOptions.find((opt) => opt.key === selectedPeriod)?.label}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      {periodOptions.map((option) => (
                        <Dropdown.Item
                          key={option.key}
                          active={selectedPeriod === option.key}
                          onClick={() => handlePeriodChange(option.key)}
                        >
                          {option.label}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                )}
              </div>
            </div>
            <div className="p-6">
              {loading && !safeAnalyticsData ? (
                <div className="flex items-center justify-center" style={{ height: '400px' }}>
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#4091CC] border-t-transparent mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading chart data...</p>
                  </div>
                </div>
              ) : !safeAnalyticsData?.lineChart ? (
                <div className="flex items-center justify-center" style={{ height: '400px' }}>
                  <div className="text-center">
                    <BarChart3 size={48} className="text-gray-400 mx-auto mb-4" />
                    <h6 className="text-gray-600 font-medium mb-2">No Line Chart Data Available</h6>
                    <p className="text-gray-500">Click "Analyze Data" to load the latest information.</p>
                  </div>
                </div>
              ) : (
                <>
                  <Chart options={lineChartOptions} series={lineChartSeries} type="line" height="400" />
                  <div className="mt-6 pt-6 border-t border-gray-100">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h6 className="text-gray-600 text-sm font-medium mb-1">Average</h6>
                        <span className="text-xl font-bold text-[#4091CC]">{Math.round(summaryStats.average).toLocaleString()}</span>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h6 className="text-gray-600 text-sm font-medium mb-1">Data Points</h6>
                        <span className="text-xl font-bold text-[#4091CC]">{safeAnalyticsData.lineChart.labels.length}</span>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h6 className="text-gray-600 text-sm font-medium mb-1">Last Updated</h6>
                        <span className="text-lg font-bold text-[#4091CC]">{new Date().toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Pie Chart */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden h-full">
            <div className="p-6 border-b border-gray-100">
              <h5 className="text-xl font-semibold text-gray-800">Platform Distribution</h5>
            </div>
            <div className="p-6">
              {loading && !safeAnalyticsData ? (
                <div className="flex items-center justify-center" style={{ height: '300px' }}>
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#4091CC] border-t-transparent mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading...</p>
                  </div>
                </div>
              ) : !safeAnalyticsData?.pieChart ? (
                <div className="flex items-center justify-center" style={{ height: '300px' }}>
                  <div className="text-center">
                    <BarChart3 size={48} className="text-gray-400 mx-auto mb-4" />
                    <h6 className="text-gray-600 font-medium mb-2">No Distribution Data</h6>
                    <p className="text-gray-500">Click "Analyze Data" to load platform data.</p>
                  </div>
                </div>
              ) : (
                <>
                  <Chart options={pieChartOptions} series={pieChartSeries} type="pie" width="100%" height="300" />
                  <div className="mt-4 space-y-3">
                    <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
                        <span className="text-gray-700 font-medium">Facebook</span>
                      </div>
                      <span className="font-bold text-blue-600">{facebookUserCount || 0}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-pink-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-pink-600 rounded-full"></div>
                        <span className="text-gray-700 font-medium">Instagram</span>
                      </div>
                      <span className="font-bold text-pink-600">{instagramUserCount || 0}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-green-600 rounded-full"></div>
                        <span className="text-gray-700 font-medium">WhatsApp</span>
                      </div>
                      <span className="font-bold text-green-600">{whatsappUserCount || 0}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-[#4091CC]/10 rounded-lg border-t-2 border-[#4091CC]">
                      <span className="text-gray-800 font-semibold">Total</span>
                      <span className="text-xl font-bold text-[#4091CC]">{(facebookUserCount || 0) + (instagramUserCount || 0) + (whatsappUserCount || 0)}</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConversationAnalytics;